import { ReactElement, ReactNode } from 'react';
import { TabsProps } from 'antd';
import { useTranslation } from 'react-i18next';
import { Outlet, matchPath, useLocation, useNavigate } from 'react-router-dom';
import { Tabs } from 'common/components/antd/Tabs/Tabs';
import { ENV_API_TYPE } from 'common/constants/env.constants';
import { ROUTES_MAPPING } from 'navigation/constants/route.constants';
import HeaderComponent from './components/Header/Header.component';
import {
  STitle,
  SHeader,
  SContent,
  SContainer,
  STabs,
} from './reporting.layout.style';

type TTab = {
  path: string;
  key: string;
  label: ReactNode;
  extraContent: ReactNode;
};

enum ERouteSectionKey {
  PaymentOperations = 'paymentOperations',
  EmissionHistory = 'emissionHistory',
  User = 'users',
  Role = 'roles',
  Account = 'accounts',
  IssueBurn = 'issueBurn',
  Audit = 'audit',
  Assets = 'assets',
}

const ReportingLayout = (): ReactElement => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const location = useLocation();

  const tabs: TTab[] = [
    {
      key: ERouteSectionKey.PaymentOperations,
      label: t('reporting.tabs.paymentOperations'),
      path: ROUTES_MAPPING.PROTECTED.REPORTING_PAYMENT_OPERATIONS,
      extraContent: null,
    },
    {
      key: ERouteSectionKey.EmissionHistory,
      label: t('reporting.tabs.emissionHistory'),
      path: ROUTES_MAPPING.PROTECTED.REPORTING_EMISSION_HISTORY,
      extraContent: null,
    },
    ...(ENV_API_TYPE ? [
      {
        key: ERouteSectionKey.Audit,
        label: t('reporting.tabs.audit'),
        path: ROUTES_MAPPING.PROTECTED.REPORTING_AUDIT,
        extraContent: null,
      },
    ] : []),
  ];

  const activeTab = tabs.find((tab) => {
    const match = matchPath(
      {
        path: tab.path,
        end: false,
        caseSensitive: false,
      },
      location.pathname,
    );

    return !!match;
  });

  const extraContent = {
    right: activeTab?.extraContent,
    left: <div style={{ width: 24 }} />,
  };

  const onChangeTab: TabsProps['onChange'] = (key) => {
    const tabChangeTo = tabs.find((tab) => tab.key === key);
    if (tabChangeTo) {
      navigate(tabChangeTo.path);
    }
  };

  return (
    <SContainer>
      <STitle>{t('reporting.title')}</STitle>
      <SHeader>
        <HeaderComponent />
      </SHeader>
      <SContent>
        <STabs>
          <Tabs
            onChange={onChangeTab}
            activeKey={activeTab?.key}
            tabBarExtraContent={extraContent}
            defaultActiveKey="1"
            items={tabs}
          />
          <Outlet />
        </STabs>
      </SContent>
    </SContainer>
  );
};

export default ReportingLayout;
